import {
  Box,
  Grid,
  Typography,
  LinearProgress,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit } from '@fortawesome/free-solid-svg-icons';
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion';
import MuiAccordionSummary, {
  AccordionSummaryProps,
} from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import { styled } from '@mui/material/styles';

import React from 'react';
import sortBy from 'sort-by';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ColumnDef } from '@tanstack/react-table';
import { IAssociates, IAssociatesPage } from '../../Interface';
import { TanstackTable } from 'Shared/Components/Mui/TanstackTable/index';
import RegisterStructure from 'Shared/Constant/Associates/RecordStructure';
import { IFieldStructure } from 'Shared/Interfaces/ObjectSalesforce';
import { ASSOCIATEDETAIL } from 'Shared/Constant/Routes';
import { KEY_ASSOCIATES_SELECTED } from 'Shared/Constant/Cache';
import ExportFilePage from './_exportfile';

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  '&:not(:last-child)': {
    borderBottom: 0,
  },
  '&:before': {
    display: 'none',
  },
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary
    expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '0.9rem' }} />}
    {...props}
  />
))(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, .05)'
      : 'rgba(0, 0, 0, .03)',
  flexDirection: 'row-reverse',
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(90deg)',
  },
  '& .MuiAccordionSummary-content': {
    marginLeft: theme.spacing(1),
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: '1px solid rgba(0, 0, 0, .125)',
}));

function AssociatesResultPage(props: IAssociatesPage) {

  const [columns, setColumns] = React.useState<ColumnDef<object, any>[]>([]);
  const toggleSetColumns = (values: ColumnDef<object, any>[]) => {
    setColumns(values);
  }

  const [data, setData] = React.useState<object[]>([]);
  const toggleSetData = (values: [IAssociates?]) => {

    const _array: object[] = values.map((reg, index) => ({
      ...(reg || {}),
      id: index + 1,
      __rowNumber: index + 1,
    }));

    setData(_array);
  }

  const [expanded, setExpanded] = React.useState(true);
  const toggleExpanded = (value: boolean) => {
    setExpanded(value);
  };

  const toggleSetItemSelected = (item: any) => {
    const _itemAssociates: IAssociates = item;

    if (props.toggleSetAssociateSelected) {
      props.toggleSetAssociateSelected(_itemAssociates);
    }
    if (props.toggleSetCache) {
      props.toggleSetCache(KEY_ASSOCIATES_SELECTED, _itemAssociates);
    }
  }

  const getFieldsStructure = React.useCallback((list: IFieldStructure[]) => {
    const _array: ColumnDef<object, any>[] = [];
    if (list && list.length > 0) {
      for (let regField of list.sort(sortBy('orderFormMain'))) {
        let _column: ColumnDef<object, any> | null = null;
        if (regField.viewFormMain) {
          switch (regField.type) {
            case 'checkbox':
              _column = {
                id: regField.nameField!,
                accessorKey: regField.nameField!,
                header: regField.label,
                cell: ({ row }) => {
                  const rowdata = row.original as any;
                  if (regField.nameField === 'active') {
                    return (<input type="checkbox" checked={rowdata.active} readOnly />)
                  }
                  else if (regField.nameField === 'isMeta') {
                    return (<input type="checkbox" checked={rowdata.isMeta} readOnly />)
                  }
                  return (<input type="checkbox" checked={Boolean(row.getValue(regField.nameField!))} readOnly />);
                },
              }
              break;
            case 'currency':
              _column = {
                id: regField.nameField!,
                accessorKey: regField.nameField!,
                header: regField.label,
                meta: {
                  align: 'right',
                },
              };
              break;
            default:
              _column = {
                id: regField.nameField!,
                accessorKey: regField.nameField!,
                header: regField.label,
              };
              break;
          }
          if (!_column || regField.hiddenFormMain) {
            continue;
          }
          if (regField.width || regField.cellStyle) {
            _column.meta = {
              ...(typeof _column.meta === 'object' ? _column.meta : {}),
              cellSx: regField.cellStyle,
              headerSx: regField.width ? { width: regField.width } : undefined,
            };
          }

          switch (regField.nameField) {
            case 'qtCxFaturada':
              _column.sortingFn = (rowA, rowB, columnId) => {
                const first = String(rowA.getValue(columnId) ?? '0');
                const second = String(rowB.getValue(columnId) ?? '0');
                return parseFloat(first.replace(/\./g, '').replace(/,/g, '.'))
                  - parseFloat(second.replace(/\./g, '').replace(/,/g, '.'));
              };
              break;
            case 'vlrVenda':
              _column.sortingFn = (rowA, rowB, columnId) => {
                const first = String(rowA.getValue(columnId) ?? '0');
                const second = String(rowB.getValue(columnId) ?? '0');
                return parseFloat(first.replace(/\./g, '').replace(/,/g, '.'))
                  - parseFloat(second.replace(/\./g, '').replace(/,/g, '.'));
              };
              break;
            case 'valorBase':
              _column.sortingFn = (rowA, rowB, columnId) => {
                const first = String(rowA.getValue(columnId) ?? '0');
                const second = String(rowB.getValue(columnId) ?? '0');
                return parseFloat(first.replace(/\./g, '').replace(/,/g, '.'))
                  - parseFloat(second.replace(/\./g, '').replace(/,/g, '.'));
              };
              // _column.render = (rowData: any) => parseFloat(rowData.valorBase.replace(/\./g, '').replace(/,/g, '.')).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', currencyDisplay: 'narrowSymbol' }).replace('R$', ''); // Formata o número no formato pt-br sem moeda
              break;
            case 'qtdeBase':
              _column.sortingFn = (rowA, rowB, columnId) => {
                const first = String(rowA.getValue(columnId) ?? '0');
                const second = String(rowB.getValue(columnId) ?? '0');
                return parseFloat(first.replace(/\./g, '').replace(/,/g, '.'))
                  - parseFloat(second.replace(/\./g, '').replace(/,/g, '.'));
              };
              break;
            case 'metaMix':
              _column.sortingFn = (rowA, rowB, columnId) => {
                const first = String(rowA.getValue(columnId) ?? '0');
                const second = String(rowB.getValue(columnId) ?? '0');
                return parseFloat(first.replace(/\./g, '').replace(/,/g, '.'))
                  - parseFloat(second.replace(/\./g, '').replace(/,/g, '.'));
              };
              break;
            case 'mix':
              _column.sortingFn = (rowA, rowB, columnId) => {
                const first = String(rowA.getValue(columnId) ?? '0');
                const second = String(rowB.getValue(columnId) ?? '0');
                return parseFloat(first.replace(/\./g, '').replace(/,/g, '.'))
                  - parseFloat(second.replace(/\./g, '').replace(/,/g, '.'));
              };
              break;
            case 'percentMix':
              _column.sortingFn = (rowA, rowB, columnId) => {
                const first = String(rowA.getValue(columnId) ?? '0');
                const second = String(rowB.getValue(columnId) ?? '0');
                return parseFloat(first.replace(/\./g, '').replace(/,/g, '.'))
                  - parseFloat(second.replace(/\./g, '').replace(/,/g, '.'));
              };
              break;
            case 'metaFaturamento':
              _column.sortingFn = (rowA, rowB, columnId) => {
                const first = String(rowA.getValue(columnId) ?? '0');
                const second = String(rowB.getValue(columnId) ?? '0');
                return parseFloat(first.replace(/\./g, '').replace(/,/g, '.'))
                  - parseFloat(second.replace(/\./g, '').replace(/,/g, '.'));
              };
              break;
            case 'faturamento':
              _column.sortingFn = (rowA, rowB, columnId) => {
                const first = String(rowA.getValue(columnId) ?? '0');
                const second = String(rowB.getValue(columnId) ?? '0');
                return parseFloat(first.replace(/\./g, '').replace(/,/g, '.'))
                  - parseFloat(second.replace(/\./g, '').replace(/,/g, '.'));
              };
              break;
            case 'percentFaturmento':
              _column.sortingFn = (rowA, rowB, columnId) => {
                const first = String(rowA.getValue(columnId) ?? '0');
                const second = String(rowB.getValue(columnId) ?? '0');
                return parseFloat(first.replace(/\./g, '').replace(/,/g, '.'))
                  - parseFloat(second.replace(/\./g, '').replace(/,/g, '.'));
              };
              break;
            case 'caixas':
              _column.sortingFn = (rowA, rowB, columnId) => {
                const first = String(rowA.getValue(columnId) ?? '0');
                const second = String(rowB.getValue(columnId) ?? '0');
                return parseFloat(first.replace(/\./g, '').replace(/,/g, '.'))
                  - parseFloat(second.replace(/\./g, '').replace(/,/g, '.'));
              };
              break;
            case 'qtUnitCx':
              _column.sortingFn = (rowA, rowB, columnId) => {
                const first = String(rowA.getValue(columnId) ?? '0');
                const second = String(rowB.getValue(columnId) ?? '0');
                return parseFloat(first.replace(/\./g, '').replace(/,/g, '.'))
                  - parseFloat(second.replace(/\./g, '').replace(/,/g, '.'));
              };
              break;
            case 'vlrUnitario':
              _column.sortingFn = (rowA, rowB, columnId) => {
                const first = String(rowA.getValue(columnId) ?? '0');
                const second = String(rowB.getValue(columnId) ?? '0');
                return parseFloat(first.replace(/\./g, '').replace(/,/g, '.'))
                  - parseFloat(second.replace(/\./g, '').replace(/,/g, '.'));
              };
              break;
            default:
              break;
          }

          _array.push(_column);
        }
      }
    }
    toggleSetColumns(_array);

  }, [])

  React.useEffect(() => {
    getFieldsStructure(RegisterStructure);
    toggleSetData(props.listAssociates);
  }, [props.listAssociates, props.dateUpdateInfo, getFieldsStructure])

  return (
    <Box id="AssociatesFilter" key="AssociatesFilter">
      <Grid container>
        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
          <Accordion
            expanded={expanded}
            onChange={() => { toggleExpanded(!expanded); }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1a-content"
              id="panel1a-header"
            >
              <Typography component="div" align="left">
                <span className="FormTitle">Resultado</span>
                {props.showLoadingUpdateTable && (
                  <Box component="span" sx={{ display: 'inline-flex', width: 120 }}>
                    <LinearProgress />
                  </Box>
                )}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography id="AssociatesResultTableExport" component="div" align="right">
                <ExportFilePage {...props} />
              </Typography>
              <Typography id="AssociatesResultTable" component="div" align="left">
                {props.showLoadingThisPage ? <LinearProgress /> :
                  <TanstackTable
                    data={data}
                    columns={columns}
                    title="Associados"
                    isLoading={props.showLoadingThisPage}
                    enableGlobalFilter
                    stickyHeader
                    rowsPerPageOptions={[5, 10, 20, 50]}
                    renderRowActions={(row) => (
                      <Link
                        color="inherit"
                        to={ASSOCIATEDETAIL}
                        onClick={() => toggleSetItemSelected(row.original)}
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </Link>
                    )}
                  />
                }
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Grid>
      </Grid>
    </Box>
  );
}

export default AssociatesResultPage;
